import type { AccountInteraction, Project } from "@baton/api";
import type { IsoDate } from "@baton/core";
import { create } from "zustand";
import type { BulkReport } from "../collection/bulk/results.ts";
import type { MoveSubject } from "../item/move/moves.ts";

/**
 * The `ui` store of `research.md` W-05: the palette's open state and query, the sidebar
 * drawer below `--bp-lg`, the shortcut reference, the New project and New item dialogs, the
 * quick-create menu with the Log a call or meeting and New client dialogs it opens, the Move to
 * project dialog (2026-09-29), and
 * the side peek's origin (the row that opened it, which `Esc` returns focus to) and the last
 * item it showed (which `Cmd+\` reopens), and whether the unverified-address banner was dismissed. No server data lives here; the query cache owns
 * that, and the URL owns anything that survives a reload, the open peek included.
 */
export interface UiState {
  readonly paletteOpen: boolean;
  /** True from the first opening on: the palette chunk mounts then and stays mounted. */
  readonly paletteMounted: boolean;
  readonly paletteQuery: string;
  readonly sidebarDrawerOpen: boolean;
  readonly shortcutsOpen: boolean;
  readonly newProjectOpen: boolean;
  /** Set when something other than the sidebar/palette/Projects index opened the dialog and wants the created project back, e.g. the New item dialog's "Create project" option (2026-09-25). */
  readonly newProjectCallback: ((project: Project) => void) | null;
  /** The New item dialog, with the project and the sprint it opens on when a screen names them (the sprint 2026-09-17, night, by the owner's report that a task made from a sprint's page did not join it), and, opened from the Clients area, its client context (`contracts/screens.md` § 020 Accounts and pipeline, the block of 2026-09-27, Defaults for a new task in the Clients area), and the assignees it opens with when the screen names them (My work's, the reader, 2026-10-03), and, opened from a task's sub-tasks through the inline row's More, the parent and what the row held (2026-10-15). */
  readonly newItem: { readonly open: boolean; readonly projectId: string | null; readonly cycleId: string | null; readonly clients: NewItemClients | null; readonly assigneeIds: readonly string[] | null; readonly subTask: NewItemSubTask | null };
  /** The Log a call or meeting dialog (the block of 2026-09-27, Log a call or meeting): the client it opens on, or null for the client picker, and the entry it opens to be read or edited, or null for a new one. */
  readonly logInteraction: { readonly open: boolean; readonly accountId: string | null; readonly entry: AccountInteraction | null; readonly mode: "new" | "read" | "edit" };
  /** The New client dialog, opened from the client list and from the quick-create menu (the block of 2026-09-27, New client). */
  readonly newClientOpen: boolean;
  /** The quick-create menu (the block of 2026-09-27, The quick-create menu), opened by the top bar's plus and by `n`. */
  readonly quickCreateOpen: boolean;
  /** The id of the item whose row opened the peek, for the focus return on close. */
  readonly peekOrigin: string | null;
  /** The item the peek showed last, for the toggle shortcut. */
  readonly lastPeek: string | null;
  /** The unverified-address banner was dismissed; it stays away until the page loads again (001 FR-153, 2026-09-28). */
  readonly verifyBannerDismissed: boolean;
  /** The Move to project dialog (2026-09-29, `item/move/MoveDialog.tsx`), open while a control asked for a move. */
  readonly moveRequest: MoveRequest | null;
  readonly setPaletteOpen: (open: boolean, query?: string) => void;
  readonly setPaletteQuery: (query: string) => void;
  readonly setSidebarDrawerOpen: (open: boolean) => void;
  readonly toggleSidebarDrawer: () => void;
  readonly setShortcutsOpen: (open: boolean) => void;
  readonly setNewProjectOpen: (open: boolean, onCreated?: (project: Project) => void) => void;
  readonly setNewItemOpen: (open: boolean, projectId?: string | null, cycleId?: string | null, clients?: NewItemClients | null, assigneeIds?: readonly string[] | null, subTask?: NewItemSubTask | null) => void;
  readonly setLogInteraction: (state: { readonly open: false } | { readonly open: true; readonly accountId: string | null; readonly entry?: AccountInteraction | null; readonly mode?: "new" | "read" | "edit" }) => void;
  readonly setNewClientOpen: (open: boolean) => void;
  readonly setQuickCreateOpen: (open: boolean) => void;
  readonly setPeekOrigin: (itemId: string | null) => void;
  readonly setLastPeek: (itemRef: string | null) => void;
  readonly dismissVerifyBanner: () => void;
  readonly setMoveRequest: (request: MoveRequest | null) => void;
}

/**
 * What a control asks the Move to project dialog for: the tasks it names, as the control read them;
 * the project already chosen, when the control chose it (the task's Project row), so the dialog opens
 * on the confirmation; and the runner the bulk bar reports its moves through, absent elsewhere.
 */
export interface MoveRequest {
  readonly subjects: readonly MoveSubject[];
  readonly targetId: string | null;
  readonly run?: (work: () => Promise<BulkReport>) => void;
}

/**
 * The New task dialog opened from the Clients area: the client it is set to (null leaves the client
 * picker empty) and the client milestone it takes (the group's whose plus opened it, else the
 * client's current one, resolved by the dialog), and, opened from a client email's Create task
 * (022 FR-022-369, 2026-10-13), the email the task is made from.
 */
export interface NewItemClients {
  readonly accountId: string | null;
  readonly milestoneStageId: string | null;
  readonly email?: NewItemEmail;
}

/**
 * The New task dialog opened from a task's sub-tasks panel, through the inline row's More (2026-10-15,
 * by the owner's decision): the parent the sub-task is made under, which fixes its project, and the
 * title, state, priority and due date the row held, so nothing typed or chosen there is lost. The
 * row's assignees travel as the dialog's `assigneeIds`, null while untouched.
 */
export interface NewItemSubTask {
  readonly parent: { readonly id: string; readonly title: string };
  readonly title: string;
  readonly stateId: string | null;
  readonly priority: string;
  readonly dueDate: IsoDate | null;
}

/** The client email a task is made from: its id, and its subject, which the dialog takes as the title. */
export interface NewItemEmail {
  readonly id: string;
  readonly subject: string;
}

export const useUiStore = create<UiState>((set) => ({
  paletteOpen: false,
  paletteMounted: false,
  paletteQuery: "",
  sidebarDrawerOpen: false,
  shortcutsOpen: false,
  newProjectOpen: false,
  newProjectCallback: null,
  newItem: { open: false, projectId: null, cycleId: null, clients: null, assigneeIds: null, subTask: null },
  logInteraction: { open: false, accountId: null, entry: null, mode: "new" },
  newClientOpen: false,
  quickCreateOpen: false,
  peekOrigin: null,
  lastPeek: null,
  verifyBannerDismissed: false,
  moveRequest: null,
  setPaletteOpen: (open, query) => {
    set(open ? { paletteOpen: true, paletteMounted: true, ...(query === undefined ? {} : { paletteQuery: query }) } : { paletteOpen: false, paletteQuery: "" });
  },
  setPaletteQuery: (paletteQuery) => {
    set({ paletteQuery });
  },
  setSidebarDrawerOpen: (sidebarDrawerOpen) => {
    set({ sidebarDrawerOpen });
  },
  toggleSidebarDrawer: () => {
    set((state) => ({ sidebarDrawerOpen: !state.sidebarDrawerOpen }));
  },
  setShortcutsOpen: (shortcutsOpen) => {
    set({ shortcutsOpen });
  },
  setNewProjectOpen: (newProjectOpen, onCreated) => {
    set({ newProjectOpen, newProjectCallback: newProjectOpen ? (onCreated ?? null) : null });
  },
  setNewItemOpen: (open, projectId = null, cycleId = null, clients = null, assigneeIds = null, subTask = null) => {
    set({ newItem: { open, projectId, cycleId, clients, assigneeIds, subTask } });
  },
  setLogInteraction: (state) => {
    set({ logInteraction: state.open ? { open: true, accountId: state.accountId, entry: state.entry ?? null, mode: state.mode ?? "new" } : { open: false, accountId: null, entry: null, mode: "new" } });
  },
  setNewClientOpen: (newClientOpen) => {
    set({ newClientOpen });
  },
  setQuickCreateOpen: (quickCreateOpen) => {
    set({ quickCreateOpen });
  },
  setPeekOrigin: (peekOrigin) => {
    set({ peekOrigin });
  },
  setLastPeek: (lastPeek) => {
    set({ lastPeek });
  },
  dismissVerifyBanner: () => {
    set({ verifyBannerDismissed: true });
  },
  setMoveRequest: (moveRequest) => {
    set({ moveRequest });
  },
}));
